import {
  createDirectFieldValueOperator,
  createFieldValueOperator,
} from '../../fieldValueFilterFactory';
import {
  CIRCUMSTANCES_FIELD,
  CORRECTIVE_ACTION_TYPE_FIELD,
  DECISION_TO_IMPOSE_FINE_FIELD,
  DELAY_REASON_FIELD,
  EFFECT_FIELD,
  EMPLOYMENT_TYPE_FIELD,
  ENVIRONMENTAL_ASPECT_FIELD,
  HAZARD_CATEGORY_FIELD,
  IMPACT_FIELD,
  PRIORITY_FIELD,
  PROJECT_STAGE_FIELD,
  INJURED_BODY_PARTS_FIELD,
  INJURY_TYPES_FIELD,
  ISSUE_VERIFICATION_FIELD,
  REJECTION_REASON_FIELD,
  ROOT_CAUSE_FIELD,
  SPILLED_SUBSTANCE_FIELD,
  WORK_TYPE_FIELD,
  REQUIREMENTS_FIELD,
  INSTALLATION_BASIS_FIELD,
  CONSTRUCTION_PRODUCT_DOCUMENTS_FIELD,
  DEVICE_DOCUMENTS_FIELD,
  ADDITIONAL_DOCUMENTS_FIELD,
} from './config';
import { ExtendedDataRow, FieldConfig } from './types';
import { useAdditionalDocuments, useCircumstances, useConstructionProductDocuments, useCorrectiveActionTypes, useDecisionsToImposeFine, useDelayReasons, useDeviceDocuments, useEffects, useEmploymentTypes, useEnvironmentalAspects, useHazardCategories, useImpacts, useInjuredBodyParts, useInjuryTypes, useInstallationBasis, useIssueVerificationItems, usePriorities, useProjectStages, useRejectionReasons, useRequirements, useRootCauses, useSpilledSubstances, useWorktypes } from 'store/fieldValue';

const createExtendedDataDirectOperator = (
  useItems: () => { items: { _id: string; label: string }[] },
  fieldConfig: FieldConfig
) =>
  createDirectFieldValueOperator({
    useItems,
    labelId: fieldConfig.labelId,
    getValueFromRow: (row: ExtendedDataRow) =>
      row.extendedData?.[fieldConfig.fieldName] ?? null,
  });

const createListFieldOperator = (
  useItems: () => { items: { _id: string; label: string }[] },
  fieldConfig: FieldConfig
) =>
  createFieldValueOperator({
    useItems,
    labelId: fieldConfig.labelId,
  });

export const getWorkTypeOperator = createListFieldOperator(
  useWorktypes,
  WORK_TYPE_FIELD
);

export const getRootCauseOperator = createListFieldOperator(
  useRootCauses,
  ROOT_CAUSE_FIELD
);

export const getHazardCategoryOperator = createListFieldOperator(
  useHazardCategories,
  HAZARD_CATEGORY_FIELD
);

export const getEnvironmentalAspectOperator =
  createExtendedDataDirectOperator(
    useEnvironmentalAspects,
    ENVIRONMENTAL_ASPECT_FIELD
  );

export const getCorrectiveActionTypeOperator =
  createExtendedDataDirectOperator(
    useCorrectiveActionTypes,
    CORRECTIVE_ACTION_TYPE_FIELD
  );

export const getDelayReasonOperator = createExtendedDataDirectOperator(
  useDelayReasons,
  DELAY_REASON_FIELD
);

export const getIssueVerificationOperator =
  createExtendedDataDirectOperator(
    useIssueVerificationItems,
    ISSUE_VERIFICATION_FIELD
  );

export const getRejectionReasonOperator = createExtendedDataDirectOperator(
  useRejectionReasons,
  REJECTION_REASON_FIELD
);

export const getEffectOperator = createExtendedDataDirectOperator(
  useEffects,
  EFFECT_FIELD
);

export const getImpactOperator = createExtendedDataDirectOperator(
  useImpacts,
  IMPACT_FIELD
);

export const getPriorityOperator = createExtendedDataDirectOperator(
  usePriorities,
  PRIORITY_FIELD
);

export const getProjectStageOperator = createExtendedDataDirectOperator(
  useProjectStages,
  PROJECT_STAGE_FIELD
);

export const getDecisionToImposeFineOperator =
  createExtendedDataDirectOperator(
    useDecisionsToImposeFine,
    DECISION_TO_IMPOSE_FINE_FIELD
  );

export const getCircumstancesOperator = createExtendedDataDirectOperator(
  useCircumstances,
  CIRCUMSTANCES_FIELD
);

export const getSpilledSubstanceOperator = createExtendedDataDirectOperator(
  useSpilledSubstances,
  SPILLED_SUBSTANCE_FIELD
);

export const getEmploymentTypeOperator = createExtendedDataDirectOperator(
  useEmploymentTypes,
  EMPLOYMENT_TYPE_FIELD
);

export const getInjuryTypesOperator = createListFieldOperator(
  useInjuryTypes,
  INJURY_TYPES_FIELD
);

export const getInjuredBodyPartsOperator = createListFieldOperator(
  useInjuredBodyParts,
  INJURED_BODY_PARTS_FIELD
);

export const getRequirementsOperator = createListFieldOperator(
  useRequirements,
  REQUIREMENTS_FIELD
);

export const getInstallationBasisOperator = createListFieldOperator(
  useInstallationBasis,
  INSTALLATION_BASIS_FIELD
);

export const getConstructionProductDocumentsOperator = createListFieldOperator(
  useConstructionProductDocuments,
  CONSTRUCTION_PRODUCT_DOCUMENTS_FIELD
);

export const getDeviceDocumentsOperator = createListFieldOperator(
  useDeviceDocuments,
  DEVICE_DOCUMENTS_FIELD
);

export const getAdditionalDocumentsOperator = createListFieldOperator(
  useAdditionalDocuments,
  ADDITIONAL_DOCUMENTS_FIELD
);

export type { FieldConfig, ExtendedDataRow } from './types';
